// src/pages/admin/ServiceCategories.tsx
import { useEffect, useState } from "react";
import { authFetch } from "../../lib/auth";

type CategoryKey = "rent" | "goods" | "salary" | "maintenance" | "rental";

type CategoryDocGuide = {
  category: CategoryKey;
  title: string;
  body: string;
  updated_at?: string;
};

const CATEGORY_LABELS: Record<CategoryKey, string> = {
  rent: "임대료/월세",
  goods: "물품대금",
  salary: "급여",
  maintenance: "관리비",
  rental: "렌탈료",
};

const ORDER: CategoryKey[] = ["rent", "goods", "salary", "maintenance", "rental"];

const DEFAULT_TITLE = "필수 제출 서류 안내";

function formatDate(date?: string) {
  if (!date) return "-";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "-";
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 서버 category 문자열을 화면용 key 로 정규화 */
function normalizeCategory(raw?: string | null): CategoryKey | null {
  const v = (raw || "").toLowerCase();
  switch (v) {
    case "rent":
      return "rent";
    case "goods":
      return "goods";
    case "salary":
      return "salary";
    case "maintenance":
    case "fee": // ← 기존 DB에 fee 로 박혀 있는 값은 모두 관리비로 본다
      return "maintenance";
    case "rental":
      return "rental";
    default:
      return null;
  }
}

/** 화면 key 를 서버에 보낼 때 category 문자열로 변환 */
function toServerCategory(key: CategoryKey): string {
  if (key === "maintenance") return "fee"; // DB 에서는 fee 로 보관
  return key;
}

export default function ServiceCategories() {
  const [items, setItems] = useState<CategoryDocGuide[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState<CategoryKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  // 초기 로드
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await authFetch("/api/admin/category-docs");
        if (!res.ok) throw new Error("서류 안내 조회 실패");
        const data = await res.json();

        const list: any[] = data?.items || [];

        const map: Partial<Record<CategoryKey, CategoryDocGuide>> = {};

        for (const row of list) {
          const k = normalizeCategory(row.category);
          if (!k) continue;

          map[k] = {
            category: k,
            title: row.title || DEFAULT_TITLE,
            body: row.body || "",
            updated_at: row.updated_at,
          };
        }

        const merged: CategoryDocGuide[] = ORDER.map((key) => {
          const existing = map[key];
          if (existing) return existing;
          return {
            category: key,
            title: DEFAULT_TITLE,
            body: "",
          };
        });

        setItems(merged);
      } catch (e: any) {
        console.error(e);
        setError(e?.message || "조회 중 오류가 발생했습니다.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const handleChangeBody = (category: CategoryKey, value: string) => {
    setItems((prev) =>
      prev.map((it) =>
        it.category === category ? { ...it, body: value } : it
      )
    );
  };

  const handleSave = async (category: CategoryKey) => {
    const item = items.find((it) => it.category === category);
    if (!item) return;

    if (!item.body.trim()) {
      alert("안내 문구를 입력해 주세요.");
      return;
    }

    setSavingKey(category);
    setError(null);
    setSavedMessage(null);

    try {
      const serverCategory = toServerCategory(category);

      const res = await authFetch(
        `/api/admin/category-docs/${encodeURIComponent(serverCategory)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: item.title || DEFAULT_TITLE,
            body: item.body,
          }),
        }
      );

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || "저장에 실패했습니다.");
      }

      const data = await res.json();
      const saved = data?.item as any | undefined;

      if (saved) {
        const savedKey = normalizeCategory(saved.category) || category;

        const normalized: CategoryDocGuide = {
          category: savedKey,
          title: saved.title || DEFAULT_TITLE,
          body: saved.body || "",
          updated_at: saved.updated_at,
        };

        setItems((prev) =>
          prev.map((it) =>
            it.category === savedKey ? { ...it, ...normalized } : it
          )
        );
        setSavedMessage("저장되었습니다.");
        setTimeout(() => setSavedMessage(null), 2000);
      }
    } catch (e: any) {
      console.error(e);
      setError(e?.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-900">
          카테고리별 첨부서류 안내 설정
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          임대료/월세, 물품대금, 급여, 관리비, 렌탈료 등 각 결제 카테고리에서 노출되는
          <span className="font-semibold"> "필수 제출 서류 안내" </span>
          문구를 수정할 수 있습니다.
          <br />
          줄바꿈(Enter)으로 나눈 각 줄이 사용자 화면에서는 목록 형태로 표시됩니다.
        </p>
      </div>

      {loading && (
        <div className="rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-500">
          불러오는 중입니다...
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {savedMessage && (
        <div className="rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {savedMessage}
        </div>
      )}

      <div className="space-y-6">
        {items.map((item) => {
          const key = item.category;
          const label = CATEGORY_LABELS[key] || item.category;

          return (
            <section
              key={key}
              className="rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold text-slate-900">
                  {label}
                </div>
                <div className="text-[11px] text-slate-400">
                  마지막 수정: {formatDate(item.updated_at)}
                </div>
              </div>

              <div className="mt-3">
                <label className="mb-1 block text-xs font-medium text-slate-700">
                  필수 제출 서류 안내 문구
                  <span className="ml-1 text-[11px] font-normal text-slate-400">
                    (줄바꿈 단위로 목록 표시)
                  </span>
                </label>
                <textarea
                  className="mt-1 w-full min-h-[90px] rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800 outline-none focus:border-indigo-400 focus:bg-white focus:ring-1 focus:ring-indigo-200"
                  value={item.body}
                  onChange={(e) => handleChangeBody(key, e.target.value)}
                  placeholder={`예)\n임대차계약서 1건 제출 시 완료로 인정됩니다.\n계약서가 많을 경우 관리비 영수증과 기존 임차료 이체내역 2건 이상을 모두 제출해야 합니다.`}
                />
              </div>

              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={() => handleSave(key)}
                  disabled={savingKey === key}
                  className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-400"
                >
                  {savingKey === key ? "저장 중..." : "저장"}
                </button>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
